import { create } from "zustand";
import i18n from "../config/i18n"; // Adjust path if necessary
import { isRtl } from "../utils/languageUtils";
import { FallbackLng, FallbackLngObjList } from "i18next"; // Import types

interface LanguageState {
  currentLanguage: string;
  currentLanguageCode: string;
  dir: "ltr" | "rtl";
  setLanguage: (language: string) => void;
}

// Helper function to get the primary fallback language
const getPrimaryFallbackLng = (fallbackLng: false | FallbackLng | undefined): string => {
  if (typeof fallbackLng === "string") {
    return fallbackLng;
  }
  if (Array.isArray(fallbackLng)) {
    return fallbackLng[0] ?? "en"; // Use first in array, default 'en'
  }
  if (typeof fallbackLng === "object" && fallbackLng !== null && !Array.isArray(fallbackLng)) {
    const fallbackObj = fallbackLng as FallbackLngObjList;
    if (fallbackObj.default && fallbackObj.default.length > 0) {
      return fallbackObj.default[0];
    }
    const firstKey = Object.keys(fallbackObj)[0];
    if (firstKey && fallbackObj[firstKey] && fallbackObj[firstKey].length > 0) {
      return fallbackObj[firstKey][0];
    }
  }
  return "en"; // Ultimate default fallback
};

// Function to get the initial state values from i18next
const getInitialI18nState = (): {
  lang: string;
  langCode: string;
  direction: "ltr" | "rtl";
} => {
  const lang = i18n.language || getPrimaryFallbackLng(i18n.options.fallbackLng);
  const langCode = lang.split("-")[0];
  const direction = isRtl(lang) ? "rtl" : "ltr";
  return { lang, langCode, direction };
};

export const useLanguageStore = create<LanguageState>((set, get) => {
  // Initialize state from i18next
  // This will use the detected language if i18n is already initialized,
  // or the fallback if not.
  const initialStateValues = getInitialI18nState();
  const state: Omit<LanguageState, "setLanguage"> = {
    currentLanguage: initialStateValues.lang,
    currentLanguageCode: initialStateValues.langCode,
    dir: initialStateValues.direction,
  };

  // Function to update language state
  const setLanguage = (language: string) => {
    const supportedLanguages = Object.keys(i18n.options.resources || {});
    let targetLanguage = language;

    if (!supportedLanguages.includes(targetLanguage)) {
      const primaryFallback = getPrimaryFallbackLng(i18n.options.fallbackLng);
      console.warn(`Language '${targetLanguage}' not configured, falling back to '${primaryFallback}'.`);
      targetLanguage = primaryFallback;
    }

    // Only proceed if the language actually needs changing
    if (targetLanguage !== get().currentLanguage) {
      i18n.changeLanguage(targetLanguage).catch((err) => {
        console.error("Failed to change language:", err);
      });
      // State update happens via the 'languageChanged' event listener
    }
  };

  // Listener for language changes triggered by i18next
  const handleLanguageChanged = (lng: string) => {
    const supportedLanguages = Object.keys(i18n.options.resources || {});
    const primaryFallback = getPrimaryFallbackLng(i18n.options.fallbackLng);
    const finalLang = supportedLanguages.includes(lng) ? lng : primaryFallback;
    const finalLangCode = finalLang.split("-")[0];
    const finalDir = isRtl(finalLang) ? "rtl" : "ltr";

    if (finalLang !== get().currentLanguage || finalLangCode !== get().currentLanguageCode || finalDir !== get().dir) {
      set({
        currentLanguage: finalLang,
        currentLanguageCode: finalLangCode,
        dir: finalDir,
      });
    }
  };

  i18n.on("languageChanged", handleLanguageChanged);

  // If i18next is not yet initialized (e.g., async detector still running),
  // its language might be different from the initial state.
  // We subscribe to the 'initialized' event to ensure the store reflects the
  // final detected language.
  if (!i18n.isInitialized) {
    const handleInitialized = () => {
      const { lang, langCode, direction } = getInitialI18nState();
      if (lang !== get().currentLanguage || langCode !== get().currentLanguageCode || direction !== get().dir) {
        set({
          currentLanguage: lang,
          currentLanguageCode: langCode,
          dir: direction,
        });
      }
      // Clean up the listener after initialization
      i18n.off("initialized", handleInitialized);
    };
    i18n.on("initialized", handleInitialized);
  }

  // Cleanup listener on store destruction
  // This is a common pattern for Zustand stores when they are not singletons
  // or if specific cleanup is needed upon component unmount when the store's lifecycle is tied to a component.
  // For a global store like this, direct cleanup might not always be explicitly called unless the app itself is unmounted.
  // Zustand's create function doesn't return a cleanup function directly from its callback in the same way useEffect does.
  // However, if this store were created within a component or context, you might manage its cleanup there.
  // For now, we ensure i18next listeners are managed.

  return {
    ...state,
    setLanguage,
  };
});

// No need for the post-creation initialization block as the logic is now inside create()
// and handles i18next's initialization lifecycle.
